import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Globe, EyeOff } from "lucide-react";
import { Modal, TextField, TextArea, NumField, SelectField, Toggle } from "./SponsorsPage";

const BLANK = {
  name: "", kind: "strategic", logo_url: "", website_url: "",
  description: "", spotlight_quote: "", published: false, sort_order: 0,
};

const KIND_OPTIONS = ["strategic", "media"];

export default function PartnersPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const r = await api.get("/admin/partners", { withCredentials: true });
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? items : items.filter((p) => p.kind === filter);

  const save = async () => {
    if (!editing.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = { ...editing };
      delete body.id; delete body.created_at; delete body.updated_at;
      if (editing.id) {
        await api.patch(`/admin/partners/${editing.id}`, body, { withCredentials: true });
      } else {
        await api.post("/admin/partners", body, { withCredentials: true });
      }
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    await api.delete(`/admin/partners/${id}`, { withCredentials: true });
    toast.success("Deleted");
    await load();
  };

  const togglePublish = async (p) => {
    await api.patch(`/admin/partners/${p.id}`,
      { ...p, published: !p.published, id: undefined, created_at: undefined, updated_at: undefined },
      { withCredentials: true });
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-overline text-aawta-pink mb-2">PARTNERS</p>
          <h1 className="font-display text-4xl text-white">Strategic & Media Partners</h1>
          <p className="text-white/50 mt-2 text-sm">Published partners appear as Partner Spotlights on the homepage.</p>
        </div>
        {editable && (
          <button data-testid="add-partner-btn" onClick={() => setEditing({ ...BLANK })} className="btn-pink">
            <Plus size={16} /> ADD PARTNER
          </button>
        )}
      </div>

      <div className="flex gap-2">
        {[["all", "ALL"], ["strategic", "STRATEGIC"], ["media", "MEDIA"]].map(([k, l]) => (
          <button key={k} data-testid={`partner-filter-${k}`} onClick={() => setFilter(k)}
            className={`text-overline px-3 py-1.5 border ${filter === k ? "border-aawta-pink text-aawta-pink" : "border-white/15 text-white/60"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Kind</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-white/40">No partners in this view.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`partner-row-${p.id}`} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-white">{p.name}</td>
                <td className="px-4 py-3 text-white/70 uppercase text-xs">{p.kind}</td>
                <td className="px-4 py-3">
                  {editable ? (
                    <button onClick={() => togglePublish(p)} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border ${p.published ? "border-aawta-pink/50 text-aawta-pink" : "border-white/20 text-white/50"}`}>
                      {p.published ? <Globe size={12} /> : <EyeOff size={12} />}
                      {p.published ? "PUBLISHED" : "DRAFT"}
                    </button>
                  ) : <span className="text-xs text-white/50">{p.published ? "Published" : "Draft"}</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {editable && (
                    <>
                      <button onClick={() => setEditing(p)} className="text-white/60 hover:text-aawta-pink mr-3"><Edit3 size={14} /></button>
                      <button onClick={() => remove(p.id)} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Partner" : "Add Partner"} onClose={() => setEditing(null)}>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="partner-name" />
            <SelectField label="Kind" value={editing.kind} onChange={(v) => setEditing({ ...editing, kind: v })} options={KIND_OPTIONS} testId="partner-kind" />
            <TextField label="Logo URL" value={editing.logo_url} onChange={(v) => setEditing({ ...editing, logo_url: v })} placeholder="https://…" testId="partner-logo" />
            <TextField label="Website URL" value={editing.website_url} onChange={(v) => setEditing({ ...editing, website_url: v })} placeholder="https://…" testId="partner-website" />
            <div className="md:col-span-2">
              <TextArea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="partner-desc" />
            </div>
            <div className="md:col-span-2">
              <TextArea label="Spotlight Quote" value={editing.spotlight_quote} onChange={(v) => setEditing({ ...editing, spotlight_quote: v })} testId="partner-quote" />
            </div>
            <NumField label="Sort Order" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: v })} testId="partner-sort" />
            <Toggle label="Published" value={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testId="partner-publish" />
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setEditing(null)} className="btn-ghost py-2.5">CANCEL</button>
            <button data-testid="save-partner-btn" onClick={save} disabled={saving} className="btn-pink disabled:opacity-50">
              {saving ? "SAVING…" : "SAVE"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
